import React from 'react';
import LocationMapContent from './LocationMapContent';
import { useLocation } from 'react-router';
import { useIntl } from 'react-intl';
import useFetchLocations from './hooks/useFetchLocations';
import { locationQueryObject } from './data/locationQueries';
import type { Location, Content } from '@plone/types';
import { isCurrentPage } from './utils/pageUtils';
import { useRelatedLocations } from './hooks/useRelatedLocations';

type LocationMapProps = { content: Content };

const oneOfThesePages = ['/de', '/en'];

// robust check, if a location is marked to be shown in the slider
const isShownInSlider = (loc: any) => {
  const v =
    loc?.show_in_slider ??
    loc?.showInSlider ??
    loc?.showinSlider ??
    loc?.showIn_Slider;

  return v === true || v === 'true' || v === 1 || v === '1';
};

const LocationMap: React.FC<LocationMapProps> = ({ content }) => {
  const location = useLocation();
  const intl = useIntl();

  const documentUrl = `/${intl.locale}/start-maps`;
  const queryObject = locationQueryObject(documentUrl);
  const locationsData: Location[] = useFetchLocations(documentUrl, queryObject);

  const isMatchingPage = isCurrentPage(location.pathname, oneOfThesePages);

  const { locations: relatedLocations } = useRelatedLocations(content);

  // if matching page, use fetched locations, otherwise filter related locations
  const resolvedLocations: Location[] = React.useMemo(
    () =>
      isMatchingPage ? locationsData : relatedLocations.filter(isShownInSlider),
    [isMatchingPage, locationsData, relatedLocations],
  );

  if (resolvedLocations.length === 0) return null;

  const routeKey = `${intl.locale}:${location.key ?? location.pathname + location.search + location.hash}`;

  return (
    <LocationMapContent
      key={routeKey}
      content={content}
      oneOfThesePages={oneOfThesePages}
      locationsData={locationsData}
    />
  );
};

export default LocationMap;
